import EventIcon from "@mui/icons-material/Event";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CopyMenuButton, {CopyMenuEntry} from "crqlar-react-common/dist/components/CopyMenuButton";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {AppSku} from "crqlar-react-common/dist/models/app/appSku";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useMarketplaceApps} from "crqlar-react-common/dist/utils/hooks/app/useMarketplaceApps";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {resolvePermittedApp} from "crqlar-react-common/dist/utils/route/permittedApp";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useLocation} from "react-router-dom";
import {surveyNpsClassLabelKey} from "../../common/uiElements/SurveyNpsClassElement";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../common/uiElements/SurveyReviewStateElement";
import {formatSurveyScore} from "../../common/uiElements/SurveyScoreElement";
import {surveyReplyLink} from "../shared/surveyListLinks";
import type {SurveySubmissionGuest} from "./export/useSurveySubmissionGuest";

interface Props {
    submission: AggregationSurveySubmissionListItem
    survey?: Survey
    /** The dialog's guest lookup, so the summary names the booking the way the guest card does. */
    guest: SurveySubmissionGuest
    /** The dialog's live state, which a review made in it has already changed. */
    reviewState: SurveyReviewState
}

/**
    Copy for the reply dialog's header, the reservation dialog's way: a plain-text summary for a colleague or a
    support ticket, the reply, guest and booking ids, and a link that opens this reply. Answers stay out: print and
    PDF carry them, with the sensitive-answer rules in one place.
*/
const SurveySubmissionCopyButton = ({submission, survey, guest, reviewState}: Props) => {
    const {t} = useLocalize();
    const organization = useOrganization();
    const organizationApps = useOrganizationApps();
    const marketplaceApps = useMarketplaceApps();
    const {pathname} = useLocation();

    const surveysAppId = resolvePermittedApp({
        organizationApps,
        marketplaceApps,
        pathname,
        matcher: {sku: AppSku.SURVEYS},
        legacyName: 'Surveys',
    })?.id;
    const link = `${window.location.origin}${surveyReplyLink(submission.id, surveysAppId)}`;

    const summary = () => {
        const booked = guest.contextRows.find(row => row.kind === 'reservation');
        const npsLabelKey = surveyNpsClassLabelKey(submission.npsClass);

        const lines: [string, string | undefined][] = [
            [t('Organization'), organization && [organization.name, organization.id && `(${organization.id})`].filter(Boolean).join(' ')],
            [t('Survey'), [
                survey?.name || submission.surveyName,
                submission.surveyVersion !== undefined ? `v${submission.surveyVersion}` : undefined,
            ].filter(Boolean).join(' · ')],
            [t('Submitted'), formatDateDMYHM(submission.createdAt)],
            [t('Guest'), [guest.name, submission.profileId].filter(Boolean).join(' · ')],
            [t('This reply'), booked ? [booked.title, booked.detail].filter(Boolean).join(' · ') : submission.sectorName],
            [t('Score'), submission.score != null ? formatSurveyScore(submission.score) : undefined],
            [t('NPS'), npsLabelKey ? t(npsLabelKey) : undefined],
            [t('Review state'), t(SURVEY_REVIEW_STATE_LABEL_KEYS[reviewState])],
            [t('Reply'), submission.id],
            [t('Link'), link],
            [t('Page'), window.location.href],
        ];
        return lines.filter(([, value]) => !!value).map(([label, value]) => `${label}: ${value}`).join('\n');
    };

    const entries = [
        {label: t('Copy summary'), Icon: SummarizeIcon, text: summary},
        {label: t('Copy reply ID'), Icon: FeedbackOutlinedIcon, text: () => submission.id},
        submission.profileId && {label: t('Copy profile ID'), Icon: PersonIcon, text: () => submission.profileId!},
        submission.reservationId && {
            label: t('Copy reservation ID'),
            Icon: EventIcon,
            text: () => submission.reservationId!,
        },
        {label: t('Copy link'), Icon: LinkIcon, text: () => link},
    ].filter(Boolean) as CopyMenuEntry[];

    return <CopyMenuButton entries={entries}/>;
};

export default SurveySubmissionCopyButton;
